import { cleanObject } from '@shared/utils';
import { del, get, post, put } from '@/redux/requests/api.js';
import createRequestFailure from '@/redux/requests/lib/createRequestFailure.js';

const normalizeFailureResponse = ({ responseShape, fallbackMessage } = {}) => {
  const result = responseShape?.data || {};

  if (result?.success !== false) {
    return responseShape;
  }

  return {
    ...responseShape,
    data: createRequestFailure({
      code: result?.code,
      message: String(result?.message || result?.error || fallbackMessage || 'Request failed').trim() || 'Request failed',
      status: result?.status || responseShape?.status || null,
      data: result?.data
    })
  };
};

const toValidationFailure = (message) => {
  return Promise.resolve(createRequestFailure({ message }));
};

export const checkCompanyNameAvailability = ({ name, id = null } = {}, callback) => {
  const normalizedName = String(name || '').trim();

  if (!normalizedName) {
    return toValidationFailure('Organization name is required');
  }

  return get('/v1/companies/check-name', {
    loader: 'checkCompanyNameAvailability',
    callback,
    onResponse: (responseShape) => {
      return normalizeFailureResponse({
        responseShape,
        fallbackMessage: 'Failed to validate organization name'
      });
    },
    query: cleanObject(
      {
        name: normalizedName,
        id: String(id || '').trim() || undefined
      },
      { strict: true }
    )
  });
};

/* The company's environments — the ids a workflow step's connectionRef can be keyed by, and the names
   the rail and run rows render. Read-only here: environments are created and renamed through the
   organization settings surface, not from Pieve. */
export const getCompanyEnvironments = () => {
  return get('/v1/companies/environments', {
    action: 'GET_COMPANY_ENVIRONMENTS',
    loader: 'getCompanyEnvironments'
  });
};

export const createCompanyEnvironment = ({ name } = {}, callback) => {
  const normalizedName = String(name || '').trim();

  if (!normalizedName) {
    return toValidationFailure('Environment name is required');
  }

  return post('/v1/companies/environments', {
    action: 'POST_COMPANY_ENVIRONMENT',
    loader: 'createCompanyEnvironment',
    callback,
    data: { name: normalizedName }
  });
};

export const renameCompanyEnvironment = ({ environmentId, name } = {}, callback) => {
  const normalizedEnvironmentId = String(environmentId || '').trim();
  const normalizedName = String(name || '').trim();

  if (!normalizedEnvironmentId || !normalizedName) {
    return toValidationFailure('Environment and name are required');
  }

  return put(`/v1/companies/environments/${encodeURIComponent(normalizedEnvironmentId)}`, {
    action: 'PUT_COMPANY_ENVIRONMENT',
    loader: 'renameCompanyEnvironment',
    callback,
    data: { name: normalizedName }
  });
};

export const deleteCompanyEnvironment = ({ environmentId } = {}, callback) => {
  const normalizedEnvironmentId = String(environmentId || '').trim();

  if (!normalizedEnvironmentId) {
    return toValidationFailure('Environment is required');
  }

  return del(`/v1/companies/environments/${encodeURIComponent(normalizedEnvironmentId)}`, {
    action: 'DELETE_COMPANY_ENVIRONMENT',
    loader: 'deleteCompanyEnvironment',
    callback,
    // The response carries only `meta.environmentId`, so the reducer needs the id it asked about.
    meta: { environmentId: normalizedEnvironmentId }
  });
};

export const getPieveUserCompanies = ({ authToken = undefined, callback = undefined, signal = undefined, userId = undefined } = {}) => {
  // `app` makes the server's repairs fix Pieve's saved organization, not Gravity's.
  return get('/v1/users/me/companies', {
    action: 'GET_PIEVE_USER_COMPANIES',
    loader: 'getPieveUserCompanies',
    callback,
    query: { app: 'pieve' },
    companyId: null,
    authToken,
    signal,
    meta: userId
      ? {
          userId
        }
      : undefined
  });
};

export const getPieveUserCompaniesOptions = ({ userId, search = undefined, includeUserSearch = undefined, includeActivityCount = undefined, recentCompanyIds = undefined, authToken = undefined, callback = undefined } = {}) => {
  const normalizedUserId = String(userId || '').trim();

  if (!normalizedUserId) {
    return toValidationFailure('userId is required');
  }

  return get(`/v1/users/${encodeURIComponent(normalizedUserId)}/companies/options`, {
    loader: 'getPieveUserCompaniesOptions',
    callback,
    authToken,
    query: cleanObject(
      {
        search: search === undefined || search === null ? undefined : String(search),
        includeUserSearch: includeUserSearch === true ? 'true' : undefined,
        includeActivityCount: includeActivityCount === true ? 'true' : undefined,
        recentCompanyIds: Array.isArray(recentCompanyIds) ? recentCompanyIds.filter(Boolean).join(',') || undefined : String(recentCompanyIds || '').trim() || undefined
      },
      { strict: true }
    )
  });
};

export const switchPieveUserCompany = ({ companyId } = {}, callback) => {
  const normalizedCompanyId = String(companyId || '').trim();

  if (!normalizedCompanyId) {
    return toValidationFailure('companyId is required');
  }

  // `app` lets the server refuse a restricted organization this staff member is not approved for in Pieve.
  return put(`/v1/users/me/companies/${encodeURIComponent(normalizedCompanyId)}`, {
    action: 'SWITCH_PIEVE_USER_COMPANY',
    loader: 'switchPieveUserCompany',
    query: { app: 'pieve' },
    // Scoped to the target without moving the tab: a refusal must leave the tab where it was.
    companyId: normalizedCompanyId,
    callback,
    meta: {
      companyId: normalizedCompanyId
    }
  });
};

export const createCompany = ({ name, ownerId, description = undefined } = {}, callback) => {
  const normalizedName = String(name || '').trim();
  const normalizedOwnerId = String(ownerId || '').trim();

  if (!normalizedName) {
    return toValidationFailure('Organization name is required');
  }

  if (!normalizedOwnerId) {
    return toValidationFailure('Missing owner id');
  }

  return post('/v1/companies', {
    loader: 'createCompany',
    callback,
    onResponse: (responseShape) => {
      return normalizeFailureResponse({
        responseShape,
        fallbackMessage: 'Failed to create organization'
      });
    },
    data: cleanObject(
      {
        name: normalizedName,
        ownerId: normalizedOwnerId,
        description: description === undefined || description === null ? undefined : String(description).trim()
      },
      { strict: true }
    )
  });
};

const submitPersonalCompanyOrganizationAction = ({ name, convert = true, action, loader } = {}, callback) => {
  const normalizedName = String(name || '').trim();

  if (!normalizedName) {
    return toValidationFailure('Organization name is required');
  }

  return post('/v1/companies/convert', {
    action,
    loader,
    callback,
    onResponse: (responseShape) => {
      return normalizeFailureResponse({
        responseShape,
        fallbackMessage: convert ? 'Failed to convert account to organization' : 'Failed to create organization'
      });
    },
    data: {
      name: normalizedName,
      convert: convert === true,
      emails: []
    }
  });
};

export const convertCompanyToOrganization = ({ name } = {}, callback) => {
  return submitPersonalCompanyOrganizationAction(
    {
      name,
      convert: true,
      action: 'CONVERT_PIEVE_COMPANY_TO_ORGANIZATION',
      loader: 'convertCompanyToOrganization'
    },
    callback
  );
};

export const createOrganizationFromPersonalAccount = ({ name } = {}, callback) => {
  return submitPersonalCompanyOrganizationAction(
    {
      name,
      convert: false,
      action: 'CREATE_PIEVE_ORGANIZATION_FROM_PERSONAL_ACCOUNT',
      loader: 'createOrganizationFromPersonalAccount'
    },
    callback
  );
};

export const updateCompanyTimezone = ({ companyId, timezone } = {}, callback) => {
  const normalizedCompanyId = String(companyId || '').trim();
  const normalizedTimezone = String(timezone || '').trim();

  if (!normalizedCompanyId || !normalizedTimezone) {
    return toValidationFailure('Organization and timezone are required');
  }

  return put('/v1/sync/batch', {
    action: 'PUT_PIEVE_COMPANY_TIMEZONE',
    loader: 'updateCompanyTimezone',
    callback,
    data: { batch: [{ type: 'company', companyId: normalizedCompanyId, path: 'timezone', value: normalizedTimezone }] }
  });
};

/* The soft tasks limit (dbo.company.settings.enableSoftTasksLimit): when it is on, workflows keep
   running after the organization has used every task in its 30-day pool. Written through the SAME
   batch-sync route MindCloud's organization settings use, because it is the same key in the same
   row: that route already refuses the write for anyone who is not a MindCloud admin, writes the
   audit log, and clears the cached task meter so the next workflow run sees the new setting. */
export const updateCompanySoftTasksLimit = ({ companyId, enabled } = {}, callback) => {
  const normalizedCompanyId = String(companyId || '').trim();

  if (!normalizedCompanyId || typeof enabled !== 'boolean') {
    return toValidationFailure('Organization and value are required');
  }

  return put('/v1/sync/batch', {
    action: 'PUT_PIEVE_COMPANY_SOFT_TASKS_LIMIT',
    loader: 'updateCompanySoftTasksLimit',
    callback,
    data: { batch: [{ type: 'company', companyId: normalizedCompanyId, path: 'settings.enableSoftTasksLimit', value: enabled }] }
  });
};
